import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EphemeralMessage from '../StudentSingleSkill/EphemeralMessage';
import MonsterNormal from '../../assets/exit-slip-monster-normal.svg?react';
import MonsterOnFire from '../../assets/exit-slip-monster-on-fire.svg?react';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { SKILL_RESOURCE_STEP_PARAM } from '../../models/Timeline';
import {
  exitSlipSkillMessageKey,
  hasSeenExitSlipSkillMessage,
  markExitSlipSkillMessageSeen,
  type ExitSlipSkillMessageVariant,
} from '../../businessLogicService/exitSlipSkillPage';
import { motion } from 'motion/react';

interface Props {
  variant: ExitSlipSkillMessageVariant;
  skillCode: string;
}

// "Trigger after 3 sec. in page once they enter": the delay EphemeralMessage animates in on, in
// seconds, as its `delay` prop takes. It hides itself five seconds after that (see its
// onAnimationComplete) — the whole point of the message being ephemeral.
const MESSAGE_DELAY_SECONDS = 3;

/**
 * The ephemeral message the skill page shows beside its exit-ticket chip, with the monster from the
 * design: a word about where the student stands on this skill's exit tickets.
 *
 * Shown once per student, per skill, per variant — see exitSlipSkillMessageKey for why the variant is
 * part of that. It is marked as seen as soon as it is scheduled rather than after it has played out:
 * the student leaving the page two seconds in has had the message offered, and the alternative is a
 * message that comes back on every visit until they happen to stay.
 *
 * It takes its place from the flex row it is dropped into rather than from coordinates of its own.
 * That is the whole point: the distance from the right edge of the header to the chip is the width of
 * everything to the chip's right, which is the result block when there is one — absent for 'on-fire' —
 * plus a chip whose own width is its wording ("1 Exit ticket passed" and "4 Exit tickets done" are not
 * the same length). No fixed offset survives all of those, so the message is a sibling of the chip and
 * lets the browser do the measuring.
 *
 * Only the bubble is positioned, and it is positioned against this wrapper, so it travels with it.
 *
 * Mount exactly one of these at a time. Both monster assets are Figma exports carrying hardcoded ids
 * (mask0_…, filter0_…, paint0_linear_…), and inlining the same SVG twice duplicates them: every
 * url(#…) in the second copy resolves to the first copy's element, so a second instance behind a
 * `display: none` leaves the visible one with no mask, no filter and no gradient.
 *
 * Which variant applies is resolveExitSlipSkillMessageVariant's decision, not this component's; this
 * one only knows what each variant says.
 */
export const SkillHeaderExitSlipMessage: FC<Props> = ({ variant, skillCode }) => {
  const userRedux = useAppSelector(selectUser);
  const navigate = useNavigate();
  const [isShowing, setIsShowing] = useState(false);

  useEffect(() => {
    if (!userRedux?.id || !skillCode) {
      return;
    }
    const key = exitSlipSkillMessageKey(userRedux.id, skillCode, variant);
    if (hasSeenExitSlipSkillMessage(key)) {
      return;
    }
    markExitSlipSkillMessageSeen(key);
    setIsShowing(true);
  }, [userRedux?.id, skillCode, variant]);

  if (!isShowing) {
    return null;
  }

  // The nudge's way through to the challenge: the same URL the dashboard's result card uses, rather
  // than a second copy of the step's own plumbing. Navigating re-enters the page through its `?step=`
  // branch, which is what asks for the Learnosity session the challenge needs.
  const goToChallenge = () => navigate(`/skill/${skillCode}?${SKILL_RESOURCE_STEP_PARAM}=skill-challenge`);

  const Monster = variant === 'on-fire' ? MonsterOnFire : MonsterNormal;

  const copy = {
    'on-fire': (
      <>
        <span>
          <b>All Exit tickets in!</b> Seems ready to try skill challenge
        </span>
        <button
          type="button"
          onClick={goToChallenge}
          className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-font-atkinson tw-text-base tw-font-bold tw-text-blue-500 tw-underline tw-decoration-2 tw-underline-offset-4"
        >
          Go there!
        </button>
      </>
    ),
    demonstrated: (
      <span>
        Done some work in here already. <b>You got this skill!</b>
      </span>
    ),
  }[variant];

  return (
    // relative, not absolute: the monster takes a place in the header's flex row — that is what puts it
    // beside the chip — and becomes the frame the bubble below is positioned against. shrink-0 so a long
    // skill name squeezes the title rather than the monster.
    <motion.div
      className="tw-relative tw-shrink-0 tw-z-10"
      initial={{ opacity: 0, scale: 0, rotateY: -90 }}
      animate={{ opacity: 1, scale: 1, rotateY: 0 }}
      transition={{
        duration: 1,
        delay: MESSAGE_DELAY_SECONDS,
        ease: [0.34, 1.56, 0.64, 1],
      }}
      onAnimationComplete={() => {
        setTimeout(() => setIsShowing(false), 6000);
      }}
    >
      <Monster className="tw-h-16 tw-w-16" aria-hidden="true" />
      <EphemeralMessage
        delay={MESSAGE_DELAY_SECONDS}
        ease={[0.34, 1.56, 0.64, 1]}
        duration={1}
        // The bubble hangs to the monster's left on every width, so its tail is on its right — the same
        // placement the Revisit message above it makes, down to the mirrored stroke. `left: auto` is
        // what stops EphemeralMessage's own left offsets, written for a message placed against the page
        // rather than against a parent, from fighting the `right` here.
        customStyle={{
          top: '81px',
          left: 'auto',
          right: '25px',
          width: '260px',
          zIndex: 10,
        }}
        stroke
        strokePosition={{ top: '-20px', right: '-3px', left: null }}
        strokeClassName="tw-scale-x-[-1]"
        text={<div className="tw-flex tw-flex-col tw-items-center tw-gap-4">{copy}</div>}
      />
    </motion.div>
  );
};

export default SkillHeaderExitSlipMessage;
